import { downloadDriveFile, withDriveToken } from '../files/drive';
import type { SupabaseClient } from '../../lib/supabase';
import { photoCaption, unresolvedPhotos } from './mailSend';

/**
 * Photos in the body of an email (docs/rich-email-design-note.md, drop B).
 *
 * A writer names a picture two ways:
 *   ![caption](photo:beach.jpg)   a file picked on Compose or Reply
 *   ![caption](drive:FILE_ID)     a copy-set photo kept in Drive
 *
 * Neither is something the mail function understands — it only knows parts
 * of the message. So before sending, this turns every reference into an
 * inline part with a Content-ID and rewrites the body to point at it:
 *   ![caption](cid:photo-1-beach.jpg@tap-hub)
 * The picture travels inside the email, the way Gmail sends a pasted one.
 *
 * Drive bytes are read here, in the browser, with the sender's own Drive
 * access. The function never talks to Drive and needs no new secret.
 */

export interface InlinePhoto {
  cid: string;
  file: File;
}

export interface ResolvedBody {
  body: string;
  inlinePhotos: InlinePhoto[];
}

const PHOTO_REF = /!\[([^[\]\n]*)\]\(((?:photo|drive):[^()\n]+)\)/g;

/**
 * A file name as it is written into a reference.
 *
 * Spaces are kept — "Screenshot 2026-09-03 at 11.51.09 PM.png" should read
 * like itself in the box. Brackets and parentheses cannot be kept: the mark
 * puts the reference between `(` and `)`, so a file called "image (2).png"
 * would end the reference early. They become underscores here and the picked
 * file is matched by the same rule, so the two always agree.
 */
export function photoRefName(name: string): string {
  return name.replace(/[()[\]\n]/g, '_');
}

/** The reference a picked file gets in the body, and a caption to start from. */
export function photoRefFor(file: File): { ref: string; caption: string } {
  return {
    ref: `photo:${photoRefName(file.name)}`,
    caption: file.name.replace(/\.[a-z0-9]+$/i, ''),
  };
}

/** The mark the toolbar types for a photo, at its own size or a chosen width. */
export function photoMark(caption: string, ref: string, width: number | null = null): string {
  const clean = caption.replace(/[[\]\n|]/g, '').trim();
  return `![${width === null ? clean : `${clean}|${String(width)}`}](${ref})`;
}

/**
 * The sizes on the Photo size picker. Hanneza's worry (3 Sep) was the August
 * nurture photos landing bigger than they did from Gmail, so the choices are
 * the widths Gmail's own picker roughly gives you, in pixels so nobody has to
 * guess what "Medium" means. "Own size" writes no width at all.
 */
export const PHOTO_SIZES: readonly { label: string; width: number | null }[] = [
  { label: 'Own size', width: null },
  { label: 'Large · 640px', width: 640 },
  { label: 'Medium · 480px', width: 480 },
  { label: 'Small · 320px', width: 320 },
];

/** Every photo mark on a line: the line is a photo line if this finds one. */
const PHOTO_LINE =
  /^(\s*)!\[([^[\]\n]*)\]\((cid:[A-Za-z0-9._@-]+|(?:photo|drive):[^()\n]+)\)(\s*)$/;

/** The width written on a photo line, null for its own size, undefined when the line is not a photo. */
export function photoLineWidth(line: string): number | null | undefined {
  const match = PHOTO_LINE.exec(line);
  if (match === null) return undefined;
  return photoCaption(match[2] ?? '').width;
}

/** The same photo line with a different size choice written on it. */
export function withPhotoWidth(line: string, width: number | null): string {
  const match = PHOTO_LINE.exec(line);
  if (match === null) return line;
  const { alt } = photoCaption(match[2] ?? '');
  return `${match[1] ?? ''}${photoMark(alt, match[3] ?? '', width)}${match[4] ?? ''}`;
}

/**
 * A picture the browser can show for a reference, without sending anything.
 *
 * A picked file has an object URL (the picker holds it). A copy set's photo
 * lives in Drive and is shared by link when it is uploaded
 * (copySetPhotos.ts), so Drive will render its own thumbnail for it — which
 * is what makes a copy set previewable at all. Hanneza, 4 Sep: "we can't see
 * a preview of the image within the Copy Library itself, just the markdown
 * tag. This makes it tough to gauge layout and sizing before sending."
 *
 * The thumbnail is only for looking at. The bytes that actually travel are
 * still read at send time, through the sender's own Drive access.
 */
export function drivePreviewUrl(ref: string): string | null {
  if (!ref.startsWith('drive:')) return null;
  const id = ref.slice('drive:'.length).trim();
  if (!/^[A-Za-z0-9_-]{10,100}$/.test(id)) return null;
  return `https://drive.google.com/thumbnail?id=${id}&sz=w640`;
}

/**
 * The picked files a body actually refers to.
 *
 * Picking a photo and then deleting its line should give the space back:
 * what travels with the email is what the body names, so that is what the
 * 10-file / 20 MB cap counts. Hanneza, 4 Sep: after resizing a photo the
 * Reply screen stopped letting her send until she refreshed the page — the
 * cap was counting every file picked during the session, including ones no
 * longer in the email, and a refresh was the only way to clear them.
 */
export function photosInBody(body: string, picked: File[]): File[] {
  const named = new Set(
    unresolvedPhotos(body)
      .filter((ref) => ref.startsWith('photo:'))
      .map((ref) => ref.slice('photo:'.length)),
  );
  return picked.filter((file) => named.has(photoRefName(file.name)));
}

function cidFor(index: number, name: string): string {
  const safe = name.replace(/[^A-Za-z0-9._-]/g, '-').slice(0, 40);
  return `photo-${String(index + 1)}-${safe}@tap-hub`;
}

/**
 * Every photo reference in the body, turned into an inline part. Throws with
 * a sentence for the person when a photo: name matches no picked file or a
 * drive: file cannot be read — the body must not go out with a hole in it.
 */
export async function resolvePhotos(
  body: string,
  picked: File[],
  client: SupabaseClient,
): Promise<ResolvedBody> {
  const refs = unresolvedPhotos(body);
  if (refs.length === 0) return { body, inlinePhotos: [] };

  const byRef = new Map<string, InlinePhoto>();
  for (const [index, ref] of refs.entries()) {
    if (ref.startsWith('photo:')) {
      const name = ref.slice('photo:'.length);
      // Matched on the written form, not the raw name, so a file with a
      // bracket in it still finds itself (photoRefName).
      const file = picked.find((f) => photoRefName(f.name) === name);
      if (file === undefined) {
        throw new Error(`The photo "${name}" is named in the email but is not attached.`);
      }
      byRef.set(ref, { cid: cidFor(index, file.name), file });
    } else {
      const id = ref.slice('drive:'.length);
      const got = await withDriveToken(client, (token) => downloadDriveFile(token, id));
      if (!got.mimeType.startsWith('image/')) {
        throw new Error(`The Drive file for "${ref}" is not an image.`);
      }
      const file = new File([got.bytes as BlobPart], got.name, { type: got.mimeType });
      byRef.set(ref, { cid: cidFor(index, got.name), file });
    }
  }

  const rewritten = body.replace(PHOTO_REF, (whole, caption: string, ref: string) => {
    const photo = byRef.get(ref);
    return photo === undefined ? whole : `![${caption}](cid:${photo.cid})`;
  });
  return { body: rewritten, inlinePhotos: [...byRef.values()] };
}
